import React, { useEffect, useState } from 'react'
import LayoutHeader from '../../layout/LayoutHeader'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { setErrorAlert, setSuccessAlert, setLoader } from '../../store/alert/alertSlice'
import { postDataAPI, getDataAPI, putDataAPI } from '../../utils/fetchData'
import { isTourActive } from '../../utils/interactiveTour'
import CustomCalendar from '../../components/Calendar/CustomCalendar'
import AppOnboarding from '../../components/AppOnboarding'

const EmailApp = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    
    // Load emails from localStorage on mount
    const [emails, setEmails] = useState(() => {
        const saved = localStorage.getItem('dizeeDetectedEmails')
        return saved ? JSON.parse(saved) : []
    })
    const [isFetching, setIsFetching] = useState(false)
    const [activeCategory, setActiveCategory] = useState('Home')
    
    // Existing shows for ticket counts and travel
    const [existingShows, setExistingShows] = useState([])
    const [loadingShows, setLoadingShows] = useState(false)
    const [expandedEmails, setExpandedEmails] = useState(new Set())
    
    // Modal state for "Add to Dizee"
    const [showAddModal, setShowAddModal] = useState(false)
    const [selectedEmail, setSelectedEmail] = useState(null)
    const [selectedApp, setSelectedApp] = useState('') // Which app to add to
    const [selectedShow, setSelectedShow] = useState('') // For ticket counts and travel
    const [createNewShow, setCreateNewShow] = useState(false) // Whether to create new show or use existing
    const [modalFormData, setModalFormData] = useState({
        // Booking fields
        venue: '',
        artist: '',
        perfDate: '',
        city: '',
        state: '',
        // Catalog fields
        songTitle: '',
        artistName: '',
        releaseDate: '',
        releaseStatus: 'upcoming',
        // Ticket Count fields
        ticketsSold: '',
        capacity: '',
        // Travel fields
        travelType: '', // 'flight' or 'hotel'
        confirmationNumber: '',
        travelDate: '',
        // Common fields
        notes: ''
    })

    const categories = [
        { id: 'Home', label: 'Home' },
        { id: 'All', label: 'All' },
        { id: 'Show Offers', label: 'Shows' },
        { id: 'Ticket Counts', label: 'Tickets' },
        { id: 'Upcoming Releases', label: 'Releases' },
        { id: 'Travel Confirmations', label: 'Travel' }
    ]

    const categoryColors = {
        'Show Offers': '#FF9500',
        'Ticket Counts': '#FFFF00',
        'Upcoming Releases': '#3B82F6',
        'Travel Confirmations': '#FF9500'
    }

    const getCategoryColor = (category) => {
        return categoryColors[category] || '#6B7280'
    }

    const getCategoryLabel = (category) => {
        const labels = {
            'Show Offers': 'OFFER',
            'Travel Confirmations': 'TRAVEL',
            'Upcoming Releases': 'RELEASE',
            'Ticket Counts': 'TICKETS'
        }
        return labels[category] || category?.toUpperCase() || 'EMAIL'
    }

    const handleFetchEmails = async () => {
        try {
            setIsFetching(true)
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/email-detection/fetch', {}, token)
            
            if (response.data?.status === 200) {
                const newEmails = response.data.data.emails || []
                // Always replace with fresh data (refetch everything)
                setEmails(newEmails)
                localStorage.setItem('dizeeDetectedEmails', JSON.stringify(newEmails))
                dispatch(setSuccessAlert(`Found ${newEmails.length} emails`))
            } else {
                dispatch(setErrorAlert(response.data?.message || 'Failed to fetch emails'))
            }
        } catch (error) {
            console.error('Error fetching emails:', error)
            if (error.response?.data?.message?.includes('Gmail not connected')) {
                dispatch(setErrorAlert('Gmail not connected. Please connect your Gmail account in Connectors first.'))
            } else {
                dispatch(setErrorAlert('Error fetching emails'))
            }
        } finally {
            setIsFetching(false)
            dispatch(setLoader(false))
        }
    }

    const handleIgnoreEmail = async (emailId) => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/email-detection/ignore', { emailId }, token)
            
            if (response.data?.status === 200) {
                const updatedEmails = emails.filter(email => email.id !== emailId)
                setEmails(updatedEmails)
                // Update localStorage
                localStorage.setItem('dizeeDetectedEmails', JSON.stringify(updatedEmails))
                dispatch(setSuccessAlert('Email ignored'))
            } else {
                dispatch(setErrorAlert(response.data?.message || 'Failed to ignore email'))
            }
        } catch (error) {
            console.error('Error ignoring email:', error)
            dispatch(setErrorAlert('Error ignoring email'))
        }
    }
    
    const loadExistingShows = async () => {
        try {
            setLoadingShows(true)
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await getDataAPI('user/getBookingsInfo', token)
            
            if (response.data?.status === 200 && response.data?.data) {
                // Filter for confirmed shows only (for ticket counts)
                const shows = (response.data.data.data || []).filter(show => 
                    show.status === 'confirmed'
                )
                setExistingShows(shows)
            }
        } catch (error) {
            console.error('Error loading shows:', error)
        } finally {
            setLoadingShows(false)
        }
    }

    const extractBookingInfo = (email) => {
        const text = `${email.subject} ${email.snippet || ''}`.toLowerCase()
        const fullText = `${email.subject} ${email.snippet || ''}`
        
        console.log('🔍 Extracting booking info from:', fullText)
        
        // Try to extract date (look for common date formats) with smart future date logic
        let perfDate = ''
        const today = new Date()
        const currentMonth = today.getMonth() // 0-11
        const currentYear = today.getFullYear()
        
        const datePatterns = [
            /(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/,  // MM/DD/YYYY or MM-DD-YYYY
            /(\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2})/,    // YYYY-MM-DD
            /(january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2},?\s+\d{4}/i,
            /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\.?\s+\d{1,2},?\s+\d{4}/i,
            // Date without year - we'll infer the year
            /(january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}\b(?!,?\s+\d{4})/i,
            /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\.?\s+\d{1,2}\b(?!,?\s+\d{4})/i,
            // Just month and year (e.g., "july 2026")
            /(january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{4}/i,
            /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\.?\s+\d{4}/i
        ]
        
        for (const pattern of datePatterns) {
            const match = fullText.match(pattern)
            if (match) {
                try {
                    let parsedDate = new Date(match[0])
                    
                    // If no year was specified, infer it
                    if (!match[0].match(/\d{4}/)) {
                        const monthNames = {
                            'january': 0, 'jan': 0, 'february': 1, 'feb': 1, 'march': 2, 'mar': 2,
                            'april': 3, 'apr': 3, 'may': 4, 'june': 5, 'jun': 5,
                            'july': 6, 'jul': 6, 'august': 7, 'aug': 7, 'september': 8, 'sep': 8,
                            'october': 9, 'oct': 9, 'november': 10, 'nov': 10, 'december': 11, 'dec': 11
                        }
                        
                        const monthMatch = match[0].toLowerCase().match(/(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i)
                        if (monthMatch) {
                            const monthNum = monthNames[monthMatch[0].toLowerCase()]
                            const dayMatch = match[0].match(/\d{1,2}/)
                            const day = dayMatch ? parseInt(dayMatch[0]) : 1
                            
                            // If the month is before current month, assume next year
                            let year = currentYear
                            if (monthNum < currentMonth) {
                                year = currentYear + 1
                            } else if (monthNum === currentMonth && day < today.getDate()) {
                                // If same month but day has passed, assume next year
                                year = currentYear + 1
                            }
                            
                            parsedDate = new Date(year, monthNum, day)
                        }
                    }
                    
                    if (!isNaN(parsedDate.getTime())) {
                        perfDate = parsedDate.toISOString().split('T')[0]
                        console.log('✅ Extracted date:', perfDate)
                        break
                    }
                } catch (e) {
                    // Continue to next pattern
                }
            }
        }
        
        // Try to extract artist first (to avoid confusing it with venue)
        let artist = ''
        
        // Pattern 1: "ARTIST at VENUE" or "ARTIST @ VENUE"
        const artistAtPattern = /([A-Z][A-Za-z\s&'\-]{2,30}?)\s+(?:at|@)\s+([A-Z][A-Za-z\s&'\-]{2,40})/i
        let artistAtMatch = fullText.match(artistAtPattern)
        if (artistAtMatch && artistAtMatch[1]) {
            const potentialArtist = artistAtMatch[1].trim()
            const falsePositives = ['new show offer', 'show offer', 'booking', 'gig', 'performance', 'venue', 'concert', 'hey', 'hi', 'hello']
            if (!falsePositives.some(fp => potentialArtist.toLowerCase().includes(fp))) {
                artist = potentialArtist
                console.log('✅ Found artist (at pattern):', artist)
            }
        }
        
        // Pattern 2: Look for common artist indicators
        const artistPatterns = [
            /(?:artist|performer|act):\s*([A-Z][A-Za-z\s&'\-]{2,30})/i,
            /([A-Z][A-Za-z\s&'\-]{2,30})\s+(?:will perform|performing|plays|playing)/i
        ]
        for (const pattern of artistPatterns) {
            const match = fullText.match(pattern)
            if (match && match[1] && !artist) {
                artist = match[1].trim()
                console.log('✅ Found artist (pattern):', artist)
                break
            }
        }
        
        // Try to extract venue - IMPROVED to avoid false positives and separate from artist
        let venue = ''
        
        // Pattern 1: "at VENUE" or "@ VENUE" (but not if it's the artist)
        const atPattern = /(?:at|@)\s+([A-Z][A-Za-z\s&'\-]{2,40})(?:\s+in\s|\s*,\s*[A-Z]|\s+toronto|\s+vancouver|\s+montreal|\s+new\s+york|\s+los\s+angeles|\s+chicago|$)/i
        let atMatch = fullText.match(atPattern)
        if (atMatch && atMatch[1]) {
            const potentialVenue = atMatch[1].trim()
            // Filter out common false positives and artist name
            const falsePositives = ['new show offer', 'show offer', 'booking', 'gig', 'performance', 'venue', 'concert']
            if (!falsePositives.some(fp => potentialVenue.toLowerCase().includes(fp)) && 
                (!artist || potentialVenue.toLowerCase() !== artist.toLowerCase())) {
                venue = potentialVenue
                console.log('✅ Found venue (at pattern):', venue)
            }
        }
        
        // Pattern 2: VENUE followed by city (e.g., "PANN toronto")
        if (!venue) {
            const venueBeforeCityPattern = /\b([A-Z][A-Za-z\s&'\-]{2,40}?)\s+(in\s+)?(toronto|vancouver|montreal|ottawa|calgary|edmonton|winnipeg|new\s+york|los\s+angeles|chicago|miami|atlanta|boston|seattle|portland|austin|nashville|denver|phoenix|dallas|houston|philadelphia|san\s+francisco|san\s+diego|las\s+vegas)/i
            const vbcMatch = fullText.match(venueBeforeCityPattern)
            if (vbcMatch && vbcMatch[1]) {
                const potentialVenue = vbcMatch[1].trim()
                // Filter out false positives and artist
                const falsePositives = ['new show offer', 'show offer', 'booking', 'gig', 'performance', 'venue', 'concert', 'hey', 'hi', 'hello', 'from', 'to', 'the', 'and']
                if (!falsePositives.some(fp => potentialVenue.toLowerCase() === fp || potentialVenue.toLowerCase().startsWith(fp + ' ')) &&
                    (!artist || potentialVenue.toLowerCase() !== artist.toLowerCase())) {
                    venue = potentialVenue
                    console.log('✅ Found venue (before city pattern):', venue)
                }
            }
        }
        
        // Pattern 3: All caps word that's not a common word (likely venue name)
        if (!venue) {
            const allCapsPattern = /\b([A-Z]{2,}(?:\s+[A-Z]{2,})*)\b/g
            let capsMatches = fullText.match(allCapsPattern)
            if (capsMatches) {
                // Filter out common all-caps words and artist
                capsMatches = capsMatches.filter(m => 
                    m.length > 2 && 
                    !['IMPORTANT', 'URGENT', 'CONFIDENTIAL', 'EDWARD', 'CONTRERAS', 'FROM', 'TO', 'RE', 'FWD'].includes(m) &&
                    (!artist || m.toLowerCase() !== artist.toLowerCase())
                )
                if (capsMatches.length > 0) {
                    venue = capsMatches[0]
                    console.log('✅ Found venue (all caps):', venue)
                }
            }
        }
        
        // If still no venue, try subject line without email prefixes
        if (!venue && email.subject) {
            const cleanSubject = email.subject
                .replace(/^(re:|fwd?:|show\s+offer:?|new\s+show\s+offer:?|booking:?)/gi, '')
                .trim()
            
            // If subject is short and looks like a venue name
            if (cleanSubject.length > 2 && cleanSubject.length < 50) {
                const words = cleanSubject.split(/\s+/)
                // If 1-3 words and starts with capital letter, and not the artist
                if (words.length >= 1 && words.length <= 3 && /^[A-Z]/.test(cleanSubject) &&
                    (!artist || cleanSubject.toLowerCase() !== artist.toLowerCase())) {
                    venue = cleanSubject
                    console.log('✅ Found venue (subject):', venue)
                }
            }
        }
        
        // Try to extract city and state/province
        let city = ''
        let state = ''
        
        // Canadian cities and provinces
        const canadianCities = {
            'toronto': 'ON',
            'vancouver': 'BC',
            'montreal': 'QC',
            'ottawa': 'ON',
            'calgary': 'AB',
            'edmonton': 'AB',
            'winnipeg': 'MB',
            'quebec city': 'QC',
            'hamilton': 'ON',
            'victoria': 'BC'
        }
        
        // US cities (common ones)
        const usCities = {
            'new york': 'NY',
            'los angeles': 'CA',
            'chicago': 'IL',
            'houston': 'TX',
            'phoenix': 'AZ',
            'philadelphia': 'PA',
            'san antonio': 'TX',
            'san diego': 'CA',
            'dallas': 'TX',
            'san francisco': 'CA',
            'austin': 'TX',
            'seattle': 'WA',
            'denver': 'CO',
            'boston': 'MA',
            'nashville': 'TN',
            'miami': 'FL',
            'atlanta': 'GA',
            'las vegas': 'NV',
            'portland': 'OR'
        }
        
        // Check for city mentions
        const allCities = { ...canadianCities, ...usCities }
        for (const [cityName, stateCode] of Object.entries(allCities)) {
            if (text.includes(cityName)) {
                city = cityName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                state = stateCode
                console.log('✅ Found city/state:', city, state)
                break
            }
        }
        
        // Look for "City, ST" or "City, State" patterns
        if (!city) {
            const locationPatterns = [
                /,\s*([A-Z][a-z\s]+),\s*([A-Z]{2})\b/,  // City, ST
                /\sin\s+([A-Z][a-z\s]+),\s*([A-Z]{2})\b/i,  // in City, ST
                /\sat\s+[^,]+,\s*([A-Z][a-z\s]+),\s*([A-Z]{2})\b/i  // at Venue, City, ST
            ]
            
            for (const pattern of locationPatterns) {
                const match = fullText.match(pattern)
                if (match) {
                    city = match[1].trim()
                    state = match[2].trim()
                    console.log('✅ Found city/state (pattern):', city, state)
                    break
                }
            }
        }
        
        // If venue contains artist name, try to separate them
        if (venue && artist && venue.toLowerCase().includes(artist.toLowerCase())) {
            // Try to extract just the venue part
            const venueParts = venue.split(new RegExp(artist, 'i'))
            if (venueParts.length > 1) {
                const cleanVenue = venueParts.filter(p => p.trim()).join(' ').trim()
                if (cleanVenue.length > 2) {
                    venue = cleanVenue
                    console.log('✅ Separated artist from venue. New venue:', venue)
                }
            }
        }
        
        console.log('📝 Extraction complete:', { venue, city, state, perfDate, artist })
        return { venue, city, state, perfDate, artist }
    }
    
    const extractReleaseInfo = (email) => {
        const text = `${email.subject} ${email.snippet || ''}`.toLowerCase()
        const fullText = `${email.subject} ${email.snippet || ''}`
        
        console.log('🎵 Extracting release info from:', fullText)
        
        // Extract song/release title - look for patterns like "releasing X" or "new song X"
        let songTitle = ''
        
        // Pattern 1: "releasing SONG" or "releasing song SONG"
        const releasingPattern = /(?:releasing|dropped?|putting\s+out)\s+(?:song\s+)?["']?([A-Za-z0-9\s\-&']+?)["']?(?:\s+on|\s+in|\s+this|,|\.|!)/i
        let match = fullText.match(releasingPattern)
        if (match && match[1]) {
            songTitle = match[1].trim()
            console.log('✅ Found song (releasing pattern):', songTitle)
        }
        
        // Pattern 2: "new song X" or "new single X"
        if (!songTitle) {
            const newSongPattern = /(?:new\s+(?:song|single|track|release|album|ep))\s+["']?([A-Za-z0-9\s\-&']+?)["']?(?:\s+on|\s+in|\s+by|,|\.|!|$)/i
            match = fullText.match(newSongPattern)
            if (match && match[1]) {
                songTitle = match[1].trim()
                console.log('✅ Found song (new song pattern):', songTitle)
            }
        }
        
        // Pattern 3: All caps word that looks like a song title
        if (!songTitle) {
            const allCapsPattern = /\b([A-Z]{2,}(?:\s+[A-Z]{2,})*)\b/g
            let capsMatches = fullText.match(allCapsPattern)
            if (capsMatches) {
                // Filter out common all-caps words
                capsMatches = capsMatches.filter(m => 
                    m.length > 2 && 
                    !['IMPORTANT', 'URGENT', 'CONFIDENTIAL', 'EDWARD', 'CONTRERAS', 'FROM', 'TO', 'RE', 'FWD', 'HEY', 'CONGRATS'].includes(m)
                )
                if (capsMatches.length > 0) {
                    songTitle = capsMatches[0]
                    console.log('✅ Found song (all caps):', songTitle)
                }
            }
        }
        
        // Fallback: clean subject line
        if (!songTitle) {
            songTitle = email.subject
                .replace(/^(re:|fwd?:|release:?|new\s+release:?)/gi, '')
                .trim()
        }
        
        // Extract release date with smart future date logic
        let releaseDate = ''
        const today = new Date()
        const currentMonth = today.getMonth()
        const currentYear = today.getFullYear()
        
        const datePatterns = [
            /(\d{1,2}(?:st|nd|rd|th)?\s+(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{4})/i,
            /(?:on|in)\s+(\d{1,2}(?:st|nd|rd|th)?\s+(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{4})/i,
            /(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/,
            /(\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2})/
        ]
        
        for (const pattern of datePatterns) {
            match = fullText.match(pattern)
            if (match) {
                try {
                    const dateStr = match[1] || match[0]
                    const parsedDate = new Date(dateStr.replace(/(\d+)(st|nd|rd|th)/, '$1'))
                    if (!isNaN(parsedDate.getTime())) {
                        releaseDate = parsedDate.toISOString().split('T')[0]
                        console.log('✅ Extracted release date:', releaseDate)
                        break
                    }
                } catch (e) {
                    // Continue
                }
            }
        }
        
        console.log('📝 Release extraction complete:', { songTitle, releaseDate })
        return { songTitle, releaseDate }
    }
    
    const handleAddToDizee = async (email) => {
        console.log('📧 Adding email to Dizee, category:', email.category)
        
        // Open modal with email data - determine which app based on category
        setSelectedEmail(email)
        
        // Load existing shows if this is a ticket count or travel email
        if (email.category === 'Ticket Counts' || email.category === 'Travel Confirmations') {
            await loadExistingShows()
        }
        
        // Determine which app to use based on category
        let appType = ''
        let initialData = {}
        
        switch (email.category) {
            case 'Show Offers':
                appType = 'bookings'
                console.log('🎫 Opening Bookings modal')
                // Extract booking information from email
                const bookingInfo = extractBookingInfo(email)
                setCreateNewShow(true) // Always creating new for show offers
                initialData = {
                    venue: bookingInfo.venue,
                    artist: bookingInfo.artist || '',  // Use extracted artist if found
                    perfDate: bookingInfo.perfDate,
                    city: bookingInfo.city,
                    state: bookingInfo.state,
                    songTitle: '',
                    artistName: '',
                    releaseDate: '',
                    releaseStatus: 'upcoming',
                    ticketsSold: '',
                    capacity: '',
                    travelType: '',
                    confirmationNumber: '',
                    travelDate: '',
                    notes: `From: ${email.from}\n\n${email.subject}\n\n${email.snippet || ''}`
                }
                break
            case 'Upcoming Releases':
                appType = 'catalog'
                console.log('🎵 Opening Catalog modal')
                const releaseInfo = extractReleaseInfo(email)
                initialData = {
                    venue: '',
                    artist: '',
                    perfDate: '',
                    city: '',
                    state: '',
                    songTitle: releaseInfo.songTitle,
                    artistName: '',
                    releaseDate: releaseInfo.releaseDate,
                    releaseStatus: 'upcoming',
                    ticketsSold: '',
                    capacity: '',
                    travelType: '',
                    confirmationNumber: '',
                    travelDate: '',
                    notes: `From: ${email.from}\n\n${email.snippet || ''}`
                }
                break
            case 'Ticket Counts':
                appType = 'tickets'
                console.log('🎟️ Opening Tickets modal')
                setCreateNewShow(false) // Default to selecting existing show
                setSelectedShow('') // Reset selection
                initialData = {
                    venue: '',
                    artist: '',
                    perfDate: '',
                    city: '',
                    state: '',
                    songTitle: '',
                    artistName: '',
                    releaseDate: '',
                    releaseStatus: 'upcoming',
                    ticketsSold: '',
                    capacity: '',
                    travelType: '',
                    confirmationNumber: '',
                    travelDate: '',
                    notes: `From: ${email.from}\n\n${email.subject}\n\n${email.snippet || ''}`
                }
                break
            case 'Travel Confirmations':
                appType = 'travel'
                console.log('✈️ Opening Travel modal')
                setCreateNewShow(false) // Default to selecting existing show
                setSelectedShow('') // Reset selection
                initialData = {
                    venue: '',
                    artist: '',
                    perfDate: '',
                    city: '',
                    state: '',
                    songTitle: '',
                    artistName: '',
                    releaseDate: '',
                    releaseStatus: 'upcoming',
                    ticketsSold: '',
                    capacity: '',
                    travelType: email.subject.toLowerCase().includes('flight') ? 'flight' : 'hotel',
                    confirmationNumber: '',
                    travelDate: '',
                    notes: `From: ${email.from}\n\n${email.subject}\n\n${email.snippet || ''}`
                }
                break
            default:
                console.log('⚠️ Unknown category, defaulting to bookings')
                appType = 'bookings'
                setCreateNewShow(true)
                initialData = {
                    venue: '',
                    artist: '',
                    perfDate: '',
                    city: '',
                    state: '',
                    songTitle: '',
                    artistName: '',
                    releaseDate: '',
                    releaseStatus: 'upcoming',
                    ticketsSold: '',
                    capacity: '',
                    travelType: '',
                    confirmationNumber: '',
                    travelDate: '',
                    notes: email.snippet || ''
                }
        }
        
        console.log('📝 Setting app type:', appType)
        console.log('📝 Initial data:', initialData)
        
        setSelectedApp(appType)
        setModalFormData(initialData)
        setShowAddModal(true)
    }
    
    const handleModalSubmit = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            let response
            
            // Submit to the appropriate endpoint based on selected app
            switch (selectedApp) {
                case 'bookings':
                    if (!modalFormData.venue || !modalFormData.artist || !modalFormData.perfDate) {
                        dispatch(setErrorAlert('Please fill in all required fields (Venue, Artist, Date)'))
                        return
                    }
                    const bookingData = {
                        venue: modalFormData.venue.trim(),
                        artist: modalFormData.artist.trim(),
                        perf_date: modalFormData.perfDate,
                        city: modalFormData.city.trim(),
                        state: modalFormData.state.trim(),
                        city_state: `${modalFormData.city.trim()}, ${modalFormData.state.trim()}`,
                        notes: modalFormData.notes.trim() || undefined
                    }
                    response = await postDataAPI('user/saveBookings', { data: [bookingData] }, token)
                    break
                    
                case 'catalog':
                    if (!modalFormData.songTitle || !modalFormData.artistName) {
                        dispatch(setErrorAlert('Please fill in all required fields (Song Title, Artist)'))
                        return
                    }
                    const catalogData = {
                        song_title: modalFormData.songTitle.trim(),
                        artist_name: modalFormData.artistName.trim(),
                        status: modalFormData.releaseStatus,
                        release_date: modalFormData.releaseDate || undefined,
                        notes: modalFormData.notes.trim() || undefined
                    }
                    response = await postDataAPI('user/saveCatalog', { data: [catalogData] }, token)
                    break
                    
                case 'tickets':
                    if (createNewShow) {
                        // Create new show first, then add ticket counts
                        if (!modalFormData.venue || !modalFormData.artist || !modalFormData.perfDate) {
                            dispatch(setErrorAlert('Please fill in show details (Venue, Artist, Date)'))
                            return
                        }
                        const newShowData = {
                            venue: modalFormData.venue.trim(),
                            artist: modalFormData.artist.trim(),
                            perf_date: modalFormData.perfDate,
                            city: modalFormData.city.trim(),
                            state: modalFormData.state.trim(),
                            city_state: `${modalFormData.city.trim()}, ${modalFormData.state.trim()}`,
                            tix_sold: modalFormData.ticketsSold ? parseInt(modalFormData.ticketsSold) : undefined,
                            sellable_cap: modalFormData.capacity ? parseInt(modalFormData.capacity) : undefined,
                            notes: modalFormData.notes.trim() || undefined
                        }
                        response = await postDataAPI('user/saveBookings', { data: [newShowData] }, token)
            } else {
                        // Update existing show with ticket counts
                        if (!selectedShow) {
                            dispatch(setErrorAlert('Please select a show'))
                            return
                        }
                        const ticketUpdate = {
                            showId: selectedShow,
                            tix_sold: modalFormData.ticketsSold,
                            sellable_cap: modalFormData.capacity
                        }
                        response = await putDataAPI('user/bulkUpdateTicketCounts', { 
                            ticketUpdates: [ticketUpdate] 
                        }, token)
                    }
                    break
                    
                case 'travel':
                    if (createNewShow) {
                        // Create new show with travel info
                        if (!modalFormData.venue || !modalFormData.artist || !modalFormData.perfDate) {
                            dispatch(setErrorAlert('Please fill in show details (Venue, Artist, Date)'))
                            return
                        }
                        const newShowWithTravel = {
                            venue: modalFormData.venue.trim(),
                            artist: modalFormData.artist.trim(),
                            perf_date: modalFormData.perfDate,
                            city: modalFormData.city.trim(),
                            state: modalFormData.state.trim(),
                            city_state: `${modalFormData.city.trim()}, ${modalFormData.state.trim()}`,
                            notes: `${modalFormData.notes}\n\nTravel: ${modalFormData.travelType} - ${modalFormData.confirmationNumber || 'No confirmation #'}`
                        }
                        response = await postDataAPI('user/saveBookings', { data: [newShowWithTravel] }, token)
                    } else {
                        // Add travel info to existing show (via notes for now)
                        if (!selectedShow) {
                            dispatch(setErrorAlert('Please select a show'))
                            return
                        }
                        // For now, we'll add travel info as notes. In future, integrate with TourManager
                        const show = existingShows.find(s => s._id === selectedShow)
                        if (show) {
                            const updatedNotes = `${show.notes || ''}\n\nTravel: ${modalFormData.travelType} - Confirmation: ${modalFormData.confirmationNumber || 'N/A'} - Date: ${modalFormData.travelDate || 'N/A'}`
                            response = await putDataAPI(`user/updateBooking/${selectedShow}`, {
                                notes: updatedNotes.trim()
                            }, token)
                        }
                    }
                    break
                    
                default:
                    dispatch(setErrorAlert('Invalid app type'))
                    return
            }
            
            if (response.data?.status === 200 || response.status === 200 || response.data?.success) {
                // Mark email as processed in backend
                await postDataAPI('user/email-detection/ignore', { emailId: selectedEmail.id }, token)
                
                dispatch(setSuccessAlert('Added to Dizee successfully'))
                // Remove from list
                const updatedEmails = emails.filter(e => e.id !== selectedEmail.id)
                setEmails(updatedEmails)
                localStorage.setItem('dizeeDetectedEmails', JSON.stringify(updatedEmails))
                // Close modal
                setShowAddModal(false)
                setSelectedEmail(null)
            } else {
                dispatch(setErrorAlert(response.data?.message || 'Failed to add to Dizee'))
            }
        } catch (error) {
            console.error('Error adding email to Dizee:', error)
            dispatch(setErrorAlert(error.response?.data?.msg || 'Error adding to Dizee'))
        }
    }
    
    const handleModalCancel = () => {
        setShowAddModal(false)
        setSelectedEmail(null)
        setSelectedApp('')
        setSelectedShow('')
        setCreateNewShow(false)
        setModalFormData({
            venue: '',
            artist: '',
            perfDate: '',
            city: '',
            state: '',
            songTitle: '',
            artistName: '',
            releaseDate: '',
            releaseStatus: 'upcoming',
            ticketsSold: '',
            capacity: '',
            travelType: '',
            confirmationNumber: '',
            travelDate: '',
            notes: ''
        })
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric'
        })
    }

    const formatTime = (dateString) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true
        })
    }

    const getFilteredEmails = () => {
        if (activeCategory === 'Home') {
            return []
        }
        if (activeCategory === 'All') {
            return emails
        }
        return emails.filter(email => email.category === activeCategory)
    }

    const filteredEmails = getFilteredEmails()

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Email Detection</span>
                        <div className="flex items-center gap-4">
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={handleFetchEmails}
                            >
                                {isFetching ? 'Fetching...' : 'Fetch Emails'}
                            </span>
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate('/email/settings')}
                            >
                                Settings
                            </span>
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => {
                                    // In tour mode, always go back to home to preserve tour state
                                    if (isTourActive()) {
                                        navigate('/');
                                    } else {
                                        navigate(-1);
                                    }
                                }}
                            >
                                Go back
                            </span>
                    </div>
                </div>


                    {/* Category Filter Tabs */}
                <div className="w-full mb-6 px-4">
                    <div className="flex overflow-x-auto scrollbar-hide">
                            {categories.map(category => (
                            <button
                                    key={category.id}
                                    onClick={() => setActiveCategory(category.id)}
                                className={`flex-shrink-0 px-4 py-2 text-[12px] transition-colors relative ${
                                        activeCategory === category.id 
                                        ? 'text-white' 
                                        : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                                }`}
                            >
                                    {category.label}
                                    {activeCategory === category.id && (
                                        <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#CDD4DB]"></div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                    {/* Email List */}
                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        {activeCategory === 'Home' ? (
                            <div className="flex flex-col gap-4 mb-8">
                                {/* Onboarding - shows when no emails detected */}
                                {emails.length === 0 && (
                                    <div className="pt-4">
                                        <AppOnboarding
                                            appName="Email"
                                            description="Connect your inbox to automatically detect show offers, ticket counts, releases, and travel confirmations."
                                            color="#CDD4DB"
                                            steps={[
                                                {
                                                    id: 'connect-gmail',
                                                    title: 'Connect your Gmail',
                                                    description: 'Sync your inbox for detection',
                                                    completed: false,
                                                    onClick: () => navigate('/connectors/google')
                                                },
                                                {
                                                    id: 'configure-detection',
                                                    title: 'Configure detection rules',
                                                    description: 'Set which emails to detect',
                                                    completed: false,
                                                    onClick: () => navigate('/email/settings')
                                                }
                                            ]}
                                            actionText="Connect Gmail"
                                            onAction={() => navigate('/connectors/google')}
                                            icon={
                                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                            }
                                        />
                                    </div>
                                )}
                                
                                <div className="mb-4 pt-8">
                                    <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5] mb-6">
                                        Dizee will detect New Show Offers, Ticket Counts, Upcoming Releases and Travel Confirmations to be added with one click. Dizee will only scan the addresses and keywords you set here.
                                    </p>
                                        </div>

                                <div
                                    onClick={() => navigate('/email/settings')}
                                    className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#CDD4DB] hover:bg-[rgba(205,212,219,0.05)] transition-all cursor-pointer"
                                >
                                    <h3 className="text-white text-[12px] font-normal mb-2">Connect Your Inbox</h3>
                                    <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                                        Sync Gmail so Dizee can detect the four core email types you rely on.
                                    </p>
                                        </div>

                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                    <h3 className="text-white text-[12px] font-normal mb-2">What Dizee Detects</h3>
                                    <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5] mb-3">
                                        Configure detection for these four email types:
                                    </p>
                                    <ul className="space-y-2 text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                                        <li>• <span className="text-[#FF9500]">Show Offers</span> from agents and promoters</li>
                                        <li>• <span className="text-[#FFFF00]">Ticket Counts</span> from promoters and venues</li>
                                        <li>• <span className="text-[#3B82F6]">Upcoming Releases</span> from labels and distributors</li>
                                        <li>• <span className="text-[#FF9500]">Travel Confirmations</span> from airlines and hotels</li>
                                    </ul>
                                        </div>

                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                    <h3 className="text-white text-[12px] font-normal mb-2">Add to Dizee</h3>
                                    <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                                        Each detected email shows with Add to Dizee or Ignore buttons, giving you full control over what enters the system.
                                    </p>
                                    </div>
                                    </div>
                        ) : filteredEmails.length === 0 ? (
                            <div className="text-left py-[60px]">
                                <h3 className="text-white text-[12px] font-semibold mb-2">
                                    No emails found
                                </h3>
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                                    {emails.length === 0 
                                        ? 'Click "Fetch Emails" to search for emails based on your detection settings.'
                                        : `No emails found in the "${activeCategory}" category.`
                                    }
                                </p>
                                {emails.length === 0 && (
                                        <button
                                        onClick={handleFetchEmails}
                                        disabled={isFetching}
                                        className="bg-[#CDD4DB] text-black px-5 py-2 rounded-lg text-[12px] hover:opacity-80 transition-opacity disabled:opacity-50"
                                    >
                                        {isFetching ? 'Fetching...' : 'Fetch Emails'}
                                        </button>
                                )}
                                    </div>
                        ) : (
                            <div className="space-y-3 pb-6">
                                {filteredEmails.map((email) => {
                                    const categoryColor = getCategoryColor(email.category)
                                    const categoryLabel = getCategoryLabel(email.category)
                                    
                                    return (
                                        <div
                                            key={email.id}
                                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4"
                                        >
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span 
                                                            className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                                                            style={{
                                                                backgroundColor: `${categoryColor}20`,
                                                                color: categoryColor,
                                                                border: `1px solid ${categoryColor}40`
                                                            }}
                                                        >
                                                            {categoryLabel}
                                                        </span>
                                                        <h3 className="text-white text-[14px] font-medium flex-1">
                                                            {email.subject || 'No Subject'}
                                                        </h3>
                                    </div>
                                                    <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-1">
                                                        From {email.from?.split('<')[0]?.trim() || email.from}
                                                    </p>
                                                    {email.snippet && (
                                                        <div className="mt-2">
                                                            <p className={`text-[rgba(255,255,255,0.6)] text-[11px] leading-relaxed ${
                                                                expandedEmails.has(email.id) ? '' : 'line-clamp-2'
                                                            }`}>
                                                                {email.snippet}
                                                            </p>
                                                            {email.snippet.length > 100 && (
                                        <button
                                                                    onClick={() => {
                                                                        const newExpanded = new Set(expandedEmails)
                                                                        if (newExpanded.has(email.id)) {
                                                                            newExpanded.delete(email.id)
                                                                        } else {
                                                                            newExpanded.add(email.id)
                                                                        }
                                                                        setExpandedEmails(newExpanded)
                                                                    }}
                                                                    className="text-[#CDD4DB] text-[10px] mt-1 hover:underline"
                                                                >
                                                                    {expandedEmails.has(email.id) ? 'Show less' : 'Show more'}
                                        </button>
                                                            )}
                                    </div>
                                                    )}
                                        </div>
                                                <div className="text-[rgba(255,255,255,0.5)] text-[11px] ml-2">
                                                    {formatDate(email.date)}
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex gap-2 pt-3 border-t border-[rgba(255,255,255,0.1)]">
                                                <button
                                                    onClick={() => handleAddToDizee(email)}
                                                    className="flex-1 bg-[#CDD4DB] text-black text-[11px] font-medium py-2 px-3 rounded-lg hover:opacity-80 transition-opacity"
                                                >
                                                    Add to Dizee
                                                </button>
                                                <button
                                                    onClick={() => handleIgnoreEmail(email.id)}
                                                    className="flex-1 bg-[rgba(255,255,255,0.05)] text-white border border-[rgba(255,255,255,0.2)] text-[11px] py-2 px-3 rounded-lg hover:border-[rgba(255,255,255,0.4)] transition-all"
                                                >
                                                    Ignore
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                                </div>
                            )}
                    </div>
                </div>
            </div>
            
            {/* Add to Dizee Modal */}
            {showAddModal && selectedEmail && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-black border border-[rgba(255,255,255,0.2)] rounded-lg w-[90%] max-w-[350px] max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-4 border-b border-[rgba(255,255,255,0.1)]">
                            <h2 className="text-white text-[14px] font-medium">
                                {selectedApp === 'bookings' && 'Add to Bookings'}
                                {selectedApp === 'catalog' && 'Add to Catalog'}
                                {selectedApp === 'tickets' && 'Add Ticket Count'}
                                {selectedApp === 'travel' && 'Add Travel Info'}
                                {!selectedApp && 'Add to Dizee'}
                            </h2>
                            <button 
                                onClick={handleModalCancel}
                                className="text-[rgba(255,255,255,0.7)] text-[14px] hover:text-white"
                            >
                                Close
                            </button>
                        </div>
                        
                        {/* Debug Info */}
                        <div className="px-4 py-2 bg-[rgba(255,149,0,0.1)] border-b border-[rgba(255,149,0,0.2)]">
                            <p className="text-[#FF9500] text-[10px]">
                                Debug: App="{selectedApp}" | Category="{selectedEmail?.category}" | CreateNew={createNewShow ? 'Yes' : 'No'}
                            </p>
                                        </div>
                        
                        {/* Modal Body - App-specific forms */}
                        <div className="p-4 space-y-4">
                            <p className="text-[rgba(255,255,255,0.7)] text-[11px]">
                                Review and confirm the information before adding to Dizee
                            </p>
                            
                            {/* BOOKINGS FORM */}
                            {selectedApp === 'bookings' && (
                                <>
                                    <div>
                                        <label className="block text-white text-[12px] font-medium mb-2">
                                            Venue <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={modalFormData.venue}
                                            onChange={(e) => setModalFormData(prev => ({ ...prev, venue: e.target.value }))}
                                            placeholder="Enter venue name..."
                                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-white text-[12px] font-medium mb-2">
                                            Artist <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={modalFormData.artist}
                                            onChange={(e) => setModalFormData(prev => ({ ...prev, artist: e.target.value }))}
                                            placeholder="Enter artist name..."
                                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                        />
                                    </div>
                                    <div>
                                        <CustomCalendar
                                            label="Performance Date *"
                                            value={modalFormData.perfDate || ''}
                                            onChange={(isoString) => {
                                                if (isoString) {
                                                    setModalFormData(prev => ({ ...prev, perfDate: isoString.split('T')[0] }))
                                                }
                                            }}
                                            theme="bookings"
                                            required={true}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-white text-[12px] font-medium mb-2">
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                value={modalFormData.city}
                                                onChange={(e) => setModalFormData(prev => ({ ...prev, city: e.target.value }))}
                                                placeholder="City"
                                                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-white text-[12px] font-medium mb-2">
                                                State
                                            </label>
                                            <input
                                                type="text"
                                                value={modalFormData.state}
                                                onChange={(e) => setModalFormData(prev => ({ ...prev, state: e.target.value }))}
                                                placeholder="State"
                                                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            
                            {/* CATALOG FORM */}
                            {selectedApp === 'catalog' && (
                                <>
                                    <div>
                                        <label className="block text-white text-[12px] font-medium mb-2">
                                            Song Title <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={modalFormData.songTitle}
                                            onChange={(e) => setModalFormData(prev => ({ ...prev, songTitle: e.target.value }))}
                                            placeholder="Enter song title..."
                                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                        />
                                </div>
                                    <div>
                                        <label className="block text-white text-[12px] font-medium mb-2">
                                            Artist Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={modalFormData.artistName}
                                            onChange={(e) => setModalFormData(prev => ({ ...prev, artistName: e.target.value }))}
                                            placeholder="Enter artist name..."
                                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                        />
                            </div>
                                    <div>
                                        <label className="block text-white text-[12px] font-medium mb-2">
                                            Release Status
                                        </label>
                                        <select
                                            value={modalFormData.releaseStatus}
                                            onChange={(e) => setModalFormData(prev => ({ ...prev, releaseStatus: e.target.value }))}
                                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#CDD4DB]"
                                        >
                                            <option value="upcoming">Upcoming</option>
                                            <option value="released">Released</option>
                                        </select>
                                    </div>
                                    <div>
                                        <CustomCalendar
                                            label="Release Date (Optional)"
                                            value={modalFormData.releaseDate || ''}
                                            onChange={(isoString) => {
                                                if (isoString) {
                                                    setModalFormData(prev => ({ ...prev, releaseDate: isoString.split('T')[0] }))
                                                } else {
                                                    setModalFormData(prev => ({ ...prev, releaseDate: '' }))
                                                }
                                            }}
                                            theme="catalog"
                                            required={false}
                                        />
                                    </div>
                        </>
                    )}

                            {/* TICKET COUNTS FORM */}
                            {selectedApp === 'tickets' && (
                                <>
                                    {/* Option to select existing show or create new */}
                                    <div className="flex items-center gap-3 mb-2">
                                        <button
                                            onClick={() => setCreateNewShow(false)}
                                            className={`flex-1 text-[11px] py-2 px-3 rounded-lg transition-all ${
                                                !createNewShow 
                                                    ? 'bg-[rgba(255,255,0,0.2)] text-[#FFFF00] border border-[#FFFF00]' 
                                                    : 'bg-[rgba(255,255,255,0.05)] text-[rgba(255,255,255,0.5)] border border-[rgba(255,255,255,0.2)]'
                                            }`}
                                        >
                                            Select Existing Show
                                        </button>
                                        <button
                                            onClick={() => setCreateNewShow(true)}
                                            className={`flex-1 text-[11px] py-2 px-3 rounded-lg transition-all ${
                                                createNewShow 
                                                    ? 'bg-[rgba(255,255,0,0.2)] text-[#FFFF00] border border-[#FFFF00]' 
                                                    : 'bg-[rgba(255,255,255,0.05)] text-[rgba(255,255,255,0.5)] border border-[rgba(255,255,255,0.2)]'
                                            }`}
                                        >
                                            Create New Show
                                        </button>
                                    </div>
                                    
                                    {!createNewShow && (
                                        <div>
                                            <label className="block text-white text-[12px] font-medium mb-2">
                                                Select Show <span className="text-red-500">*</span>
                                            </label>
                                            {loadingShows ? (
                                                <div className="text-[rgba(255,255,255,0.5)] text-[11px] py-2">Loading shows...</div>
                                            ) : existingShows.length === 0 ? (
                                                <div className="text-[rgba(255,255,255,0.5)] text-[11px] py-2">No upcoming shows found. Create a new show instead.</div>
                                            ) : (
                                                <select
                                                    value={selectedShow}
                                                    onChange={(e) => setSelectedShow(e.target.value)}
                                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#CDD4DB]"
                                                >
                                                    <option value="">Select a show...</option>
                                                    {existingShows.map(show => (
                                                        <option key={show._id} value={show._id}>
                                                            {show.venue} - {new Date(show.perf_date).toLocaleDateString()} ({show.city_state})
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    )}
                                    
                                    {createNewShow && (
                                        <>
                                            <div>
                                                <label className="block text-white text-[12px] font-medium mb-2">
                                                    Venue <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={modalFormData.venue}
                                                    onChange={(e) => setModalFormData(prev => ({ ...prev, venue: e.target.value }))}
                                                    placeholder="Enter venue name..."
                                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-white text-[12px] font-medium mb-2">
                                                    Artist <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={modalFormData.artist}
                                                    onChange={(e) => setModalFormData(prev => ({ ...prev, artist: e.target.value }))}
                                                    placeholder="Enter artist name..."
                                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                />
                                            </div>
                                            <div>
                                                <CustomCalendar
                                                    label="Performance Date *"
                                                    value={modalFormData.perfDate || ''}
                                                    onChange={(isoString) => {
                                                        if (isoString) {
                                                            setModalFormData(prev => ({ ...prev, perfDate: isoString.split('T')[0] }))
                                                        }
                                                    }}
                                                    theme="bookings"
                                                    required={true}
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-white text-[12px] font-medium mb-2">City</label>
                                                    <input
                                                        type="text"
                                                        value={modalFormData.city}
                                                        onChange={(e) => setModalFormData(prev => ({ ...prev, city: e.target.value }))}
                                                        placeholder="City"
                                                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-white text-[12px] font-medium mb-2">State</label>
                                                    <input
                                                        type="text"
                                                        value={modalFormData.state}
                                                        onChange={(e) => setModalFormData(prev => ({ ...prev, state: e.target.value }))}
                                                        placeholder="State"
                                                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    
                                    {/* Ticket Count Fields (always shown) */}
                                    <div className="pt-3 border-t border-[rgba(255,255,255,0.1)]">
                                        <div>
                                            <label className="block text-white text-[12px] font-medium mb-2">
                                                Tickets Sold
                                            </label>
                                            <input
                                                type="number"
                                                value={modalFormData.ticketsSold}
                                                onChange={(e) => setModalFormData(prev => ({ ...prev, ticketsSold: e.target.value }))}
                                                placeholder="Enter number..."
                                                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                            />
                        </div>
                                        <div className="mt-3">
                                            <label className="block text-white text-[12px] font-medium mb-2">
                                                Sellable Capacity
                                            </label>
                                            <input
                                                type="number"
                                                value={modalFormData.capacity}
                                                onChange={(e) => setModalFormData(prev => ({ ...prev, capacity: e.target.value }))}
                                                placeholder="Enter capacity..."
                                                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            
                            {/* TRAVEL FORM */}
                            {selectedApp === 'travel' && (
                                <>
                                    {/* Option to select existing show or create new */}
                                    <div className="flex items-center gap-3 mb-2">
                                            <button
                                            onClick={() => setCreateNewShow(false)}
                                            className={`flex-1 text-[11px] py-2 px-3 rounded-lg transition-all ${
                                                !createNewShow 
                                                    ? 'bg-[rgba(255,149,0,0.2)] text-[#FF9500] border border-[#FF9500]' 
                                                    : 'bg-[rgba(255,255,255,0.05)] text-[rgba(255,255,255,0.5)] border border-[rgba(255,255,255,0.2)]'
                                            }`}
                                        >
                                            Select Existing Show
                                        </button>
                                        <button
                                            onClick={() => setCreateNewShow(true)}
                                            className={`flex-1 text-[11px] py-2 px-3 rounded-lg transition-all ${
                                                createNewShow 
                                                    ? 'bg-[rgba(255,149,0,0.2)] text-[#FF9500] border border-[#FF9500]' 
                                                    : 'bg-[rgba(255,255,255,0.05)] text-[rgba(255,255,255,0.5)] border border-[rgba(255,255,255,0.2)]'
                                            }`}
                                        >
                                            Create New Show
                                            </button>
                                        </div>
                                    
                                    {!createNewShow && (
                                        <div>
                                            <label className="block text-white text-[12px] font-medium mb-2">
                                                Select Show <span className="text-red-500">*</span>
                                            </label>
                                            {loadingShows ? (
                                                <div className="text-[rgba(255,255,255,0.5)] text-[11px] py-2">Loading shows...</div>
                                            ) : existingShows.length === 0 ? (
                                                <div className="text-[rgba(255,255,255,0.5)] text-[11px] py-2">No upcoming shows found. Create a new show instead.</div>
                                            ) : (
                                                <select
                                                    value={selectedShow}
                                                    onChange={(e) => setSelectedShow(e.target.value)}
                                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#CDD4DB]"
                                                >
                                                    <option value="">Select a show...</option>
                                                    {existingShows.map(show => (
                                                        <option key={show._id} value={show._id}>
                                                            {show.venue} - {new Date(show.perf_date).toLocaleDateString()} ({show.city_state})
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                    </div>
                                    )}
                                    
                                    {createNewShow && (
                                        <>
                                            <div>
                                                <label className="block text-white text-[12px] font-medium mb-2">
                                                    Venue <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={modalFormData.venue}
                                                    onChange={(e) => setModalFormData(prev => ({ ...prev, venue: e.target.value }))}
                                                    placeholder="Enter venue name..."
                                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-white text-[12px] font-medium mb-2">
                                                    Artist <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={modalFormData.artist}
                                                    onChange={(e) => setModalFormData(prev => ({ ...prev, artist: e.target.value }))}
                                                    placeholder="Enter artist name..."
                                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                />
                                            </div>
                                            <div>
                                                <CustomCalendar
                                                    label="Performance Date *"
                                                    value={modalFormData.perfDate || ''}
                                                    onChange={(isoString) => {
                                                        if (isoString) {
                                                            setModalFormData(prev => ({ ...prev, perfDate: isoString.split('T')[0] }))
                                                        }
                                                    }}
                                                    theme="bookings"
                                                    required={true}
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-white text-[12px] font-medium mb-2">City</label>
                                                    <input
                                                        type="text"
                                                        value={modalFormData.city}
                                                        onChange={(e) => setModalFormData(prev => ({ ...prev, city: e.target.value }))}
                                                        placeholder="City"
                                                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-white text-[12px] font-medium mb-2">State</label>
                                                    <input
                                                        type="text"
                                                        value={modalFormData.state}
                                                        onChange={(e) => setModalFormData(prev => ({ ...prev, state: e.target.value }))}
                                                        placeholder="State"
                                                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                                    />
                                                </div>
                                            </div>
                        </>
                    )}
                                    
                                    {/* Travel Info Fields (always shown) */}
                                    <div className="pt-3 border-t border-[rgba(255,255,255,0.1)]">
                                        <div>
                                            <label className="block text-white text-[12px] font-medium mb-2">
                                                Travel Type
                                            </label>
                                            <select
                                                value={modalFormData.travelType}
                                                onChange={(e) => setModalFormData(prev => ({ ...prev, travelType: e.target.value }))}
                                                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#CDD4DB]"
                                            >
                                                <option value="flight">Flight</option>
                                                <option value="hotel">Hotel</option>
                                            </select>
                </div>
                                        <div className="mt-3">
                                            <label className="block text-white text-[12px] font-medium mb-2">
                                                Confirmation Number
                                            </label>
                                            <input
                                                type="text"
                                                value={modalFormData.confirmationNumber}
                                                onChange={(e) => setModalFormData(prev => ({ ...prev, confirmationNumber: e.target.value }))}
                                                placeholder="Enter confirmation #..."
                                                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB]"
                                            />
                                        </div>
                                        <div className="mt-3">
                                            <CustomCalendar
                                                label="Travel Date"
                                                value={modalFormData.travelDate || ''}
                                                onChange={(isoString) => {
                                                    if (isoString) {
                                                        setModalFormData(prev => ({ ...prev, travelDate: isoString.split('T')[0] }))
                                                    } else {
                                                        setModalFormData(prev => ({ ...prev, travelDate: '' }))
                                                    }
                                                }}
                                                theme="bookings"
                                                required={false}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            
                            {/* Common Notes Field */}
                            <div>
                                <label className="block text-white text-[12px] font-medium mb-2">
                                    Notes (Optional)
                                </label>
                                <textarea
                                    value={modalFormData.notes}
                                    onChange={(e) => setModalFormData(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Add any additional notes..."
                                    rows={4}
                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.4)] focus:outline-none focus:border-[#CDD4DB] resize-none"
                                />
                            </div>
                        </div>
                        
                        {/* Modal Footer */}
                        <div className="flex gap-3 p-4 border-t border-[rgba(255,255,255,0.1)]">
                                            <button
                                onClick={handleModalCancel}
                                className="flex-1 bg-[rgba(255,255,255,0.05)] text-white border border-[rgba(255,255,255,0.2)] text-[12px] py-2 px-4 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-all"
                                            >
                                Cancel
                                            </button>
                                        <button
                                onClick={handleModalSubmit}
                                className="flex-1 bg-[#CDD4DB] text-black text-[12px] font-medium py-2 px-4 rounded-lg hover:opacity-80 transition-opacity"
                                        >
                                Submit
                                        </button>
            </div>
                    </div>
                </div>
            )}
        </LayoutHeader>
    )
}

export default EmailApp 
